import { filter, includes, isEmpty, map, pickBy } from 'lodash'
import { createSelector } from 'reselect'
import { GetProgressResp } from '@src/endpoints/services/commonHdr.service'
import type { CloudTypeEnum } from '@src/endpoints/services/base.service'
import type { State } from '@src/redux/configureStore'
import { Auth } from '@src/redux/reducers'
import type {
  CloudAccount,
  Folder,
  Messages,
  OrgAccount,
  OrgStructure,
  ScopeAccount
} from '@src/redux/reducers/organization/models'
import type {
  CloudAccounts,
  OrgStructures
} from '@src/redux/reducers/organization/summary'
import { FilterScopeAccountsType } from '../models'
import mapToDictionary from '@src/utils/dictionary'

export const getMessages = (state: State) =>
  state?.organization?.editor?.messages || ({} as Messages)

export const getScopeAccounts = (state: State) =>
  state?.organization?.editor?.scopeAccounts

const getScopeAccountsListByFilter = (
  scopeAccounts: ScopeAccount[],
  searchValue: string,
  condition: (x: ScopeAccount) => boolean = x => true
) => {
  const filtered = filter(
    scopeAccounts || [],
    x =>
      condition(x) &&
      includes(x?.name?.toLowerCase(), searchValue?.toLowerCase())
  )

  return mapToDictionary<ScopeAccount, ScopeAccount>({
    array: filtered || [],
    key: 'id'
  })
}

const getCheckedScopeAccountsByFilter = (
  checked: {
    [key: string]: boolean
  },
  dict: {
    [key: string]: ScopeAccount
  },
  condition: (v: boolean) => boolean = v => true
) => pickBy(checked, (x, id) => condition(x) && dict[id])

export const getScopeAccountsSelector = (
  searchValue: string,
  checked: {
    [key: string]: boolean
  },
  filterValue: FilterScopeAccountsType
) =>
  createSelector([getScopeAccounts], scopeAccounts => {
    switch (filterValue) {
      case 'selected': {
        const dict = getScopeAccountsListByFilter(
          scopeAccounts,
          searchValue,
          x => !!checked[x.id]
        )
        return {
          checked: getCheckedScopeAccountsByFilter(checked, dict, o => !!o),
          dict
        }
      }
      case 'deselected': {
        const dict = getScopeAccountsListByFilter(
          scopeAccounts,
          searchValue,
          x => !checked[x.id]
        )
        return {
          checked: getCheckedScopeAccountsByFilter(checked, dict, o => !o),
          dict
        }
      }
      case 'all':
      default: {
        const dict = getScopeAccountsListByFilter(scopeAccounts, searchValue)
        return {
          checked: getCheckedScopeAccountsByFilter(checked, dict),
          dict
        }
      }
    }
  })

export const getCountScopeAccounts = (state: State) =>
  state?.organization?.editor?.scopeAccounts?.length

export const getEnableScopeAccounts = (state: State) =>
  state?.organization?.editor?.enableScopeAccounts

export const getValidateType = (state: State) =>
  state.organization.editor.validateType
export const getIsTesting = (state: State) =>
  state.organization.editor.isTesting
export const getIsTestConnectionSuccess = (state: State) =>
  state.organization.editor.testConnectionSuccess
export const getIsFetching = (state: State) =>
  state.organization.editor.isFetching
export const getEditor = (state: State) => state.organization.editor
export const getOrgBuildUuid = (state: State) =>
  state.organization.editor.org_build_uuid

export const getIsNew = (state: State) => state.organization.editor.isNew
export const getIsScopes = (state: State) => state.organization.editor.isScopes
export const getScopesSearch = (state: State) =>
  state.organization.editor.scopesData.search
export const getScopesFilter = (state: State) =>
  state.organization.editor.scopesData.filter
export const getScopesSelect = (state: State) =>
  state.organization.editor.scopesData.select
export const isOpenEditor = (state: State) =>
  !isEmpty(state?.organization?.editor.model)

export const getEditModel = (state: State) => state.organization.editor.model
export const getIsOrg = (state: State) => state.organization.editor.isOrg

export const getFileName = (state: State) => state.organization.editor.fileName

export const getProgressFileLoad = (state: State) =>
  state.organization.editor.progressFileLoad

export const getOrganizationSummaryLastUpdate = (state: State) =>
  state.organization.summary.lastUpdate
export const getPending = (state: State) => state.organization.summary.isPending
export const getOrganizations = (state: State) =>
  state.organization.summary.structures || ({} as OrgStructures)

export const getAccounts = (state: State) =>
  state.organization.summary.accounts || ({} as CloudAccounts)

export const getPendingProgress = (state: State) => state.auth.isPendingProgress

export const getOrganizationDetailData = (state: State) =>
  state.organization.details.structure || ({} as OrgStructure)

export const getProgressList = (state: State) =>
  state?.auth?.progressList || ({} as GetProgressResp)

export const getSnapshotState = (state: State) =>
  state?.auth?.snapshotState || {}

export const getAccountStats = (state: State) =>
  state.auth.accountStats || ({} as Auth.AccountStatsDict)

export const getUserRights = (state: State) => state.auth?.user?.rights
export const getSourcesImprovementsVal = (state: State) =>
  state.auth?.sourcesImprovementsFlag

const getAccountsInProcessSelector = () =>
  createSelector([getProgressList], progressList => {
    const orgInProgress = []
    progressList?.organization_sources?.forEach(orgSource => {
      if (!orgSource?.current_phase_enum?.endsWith('_FAILED')) {
        orgSource?.progress_list?.forEach(source => {
          if (source?.progress < 100) {
            orgInProgress.push(source)
          }
        })
      }
    })

    const accountInProgress = []

    progressList?.sources?.forEach(accountSource => {
      if (
        !accountSource?.current_phase_enum?.endsWith('_FAILED') &&
        accountSource?.progress <= 100
      ) {
        accountInProgress.push(accountSource)
      }
    })

    const isInProgress =
      orgInProgress.length > 0 || accountInProgress.length > 0
    if (!isInProgress) {
      return {}
    }
    return accountInProgress.concat(orgInProgress)
  })

function getProcessById(id: string, progressList: GetProgressResp): number {
  const accountInProcess =
    progressList[id] || Object.values(progressList || { a: [] })[0]
  return accountInProcess ? accountInProcess?.progress || 0 : 100
}

function withProccesAccount(
  data: OrgAccount,
  progressList: GetProgressResp
): OrgAccount {
  return {
    ...data,
    progress: getProcessById(data?.account_id, progressList)
  }
}

function withProccesInFolder(
  data: Folder,
  progressList: GetProgressResp
): Folder {
  return {
    ...data,
    folders: map(data.folders, f => withProccesInFolder(f, progressList)),
    accounts: map(data.accounts, ac => withProccesAccount(ac, progressList))
  }
}

function getProccessOrganization(
  data: OrgStructure = {} as OrgStructure,
  progressList: GetProgressResp
): OrgStructure {
  return {
    ...data,
    progress: getProcessById(data?.original?.organization_id, progressList),
    folders: map(data.folders, f => withProccesInFolder(f, progressList)),
    accounts: map(data.accounts, ac => withProccesAccount(ac, progressList))
  }
}

function getProccessAccount(
  data: CloudAccount = {} as CloudAccount,
  progressList: GetProgressResp
): CloudAccount {
  return {
    ...data,
    progress: getProcessById(data?.original?.account_id, progressList)
  }
}

interface GetDataWithProccess<T, R> {
  data: T
  tab: CloudTypeEnum
  progressList: GetProgressResp
  progressMethod: (data: R, progressList: GetProgressResp) => R
  getId: (a: R) => string
}

function getDataWithProccess<T, R>({
  data,
  tab,
  progressList,
  getId,
  progressMethod
}: GetDataWithProccess<T, R>) {
  const accountsList: R[] = (data[tab] || []).map(a =>
    progressMethod(a, progressList)
  )
  return (accountsList || []) as R[]
}

export const getAccountsSelector = (tab: CloudTypeEnum) =>
  createSelector(
    [getAccountsInProcessSelector(), getOrganizations, getAccounts],
    (progressList, organizations, cloudAccounts) => ({
      accountsList: getDataWithProccess({
        data: cloudAccounts,
        tab,
        progressList,
        progressMethod: getProccessAccount,
        getId: a => a.original.account_id
      }),
      organizationList: getDataWithProccess({
        data: organizations,
        tab,
        progressList,
        progressMethod: getProccessOrganization,
        getId: a => a.original.organization_id
      })
    })
  )

export const getOrganizationDetailSelector = () =>
  createSelector(
    [getAccountsInProcessSelector(), getOrganizationDetailData],
    (progressList, data) => getProccessOrganization(data, progressList)
  )

export const getSourcesImprovementsSelector = () =>
  createSelector(
    [getSourcesImprovementsVal],
    getSourcesImprovementsVal => getSourcesImprovementsVal
  )
